/**
 * Permission-aware landing + Dashboard guard.
 *
 * The Dashboard is the historical default landing, but its widgets all
 * need `dashboard:read` (overview / timeseries / latency / …). A
 * low-privilege operator dropped there just saw a wall of 403s — the
 * route was reachable while the data was not. These two helpers keep the
 * navigable surface and the permission surface in agreement:
 *
 *   - `LandingRedirect` resolves `/` to the Dashboard when allowed,
 *     otherwise to the caller's first accessible page.
 *   - `RequireDashboard` guards the Dashboard route itself, bouncing a
 *     caller without `dashboard:read` to that same first page.
 *
 * Both wait for `/me` to load before deciding (render nothing meanwhile)
 * so we never flash the 403-laden Dashboard before redirecting.
 */
import * as React from "react";
import { Navigate } from "react-router-dom";

import { firstAccessiblePath } from "@/components/layout/Sidebar";
import { usePermissions } from "@/lib/permissions";

export function LandingRedirect(): React.ReactElement | null {
  const { loading, canSee } = usePermissions();
  if (loading) return null;
  const to = canSee("dashboard") ? "/dashboard" : firstAccessiblePath(canSee);
  return <Navigate to={to} replace />;
}

export function RequireDashboard({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement | null {
  const { loading, canSee } = usePermissions();
  if (loading) return null;
  if (!canSee("dashboard")) return <Navigate to={firstAccessiblePath(canSee)} replace />;
  return <>{children}</>;
}
